import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('skit_user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  // Backend-computed — true only when APP_ENV !== 'production'. The Dev User
  // Switcher button being hidden is never the only thing gating this; the
  // switch endpoint itself re-checks the same flag server-side.
  const [devUserSwitchEnabled, setDevUserSwitchEnabled] = useState(false);

  useEffect(() => {
    // Always check — a shared cross-subdomain cookie may carry a valid session
    // even when this origin's localStorage has never seen a token (e.g. landing
    // directly on materialwatch.sptlmep.in right after logging in on the portal).
    authApi.me()
      .then((res) => {
        const { devUserSwitchEnabled: switchEnabled, ...u } = res.data.data;
        setUser(u);
        setDevUserSwitchEnabled(!!switchEnabled);
        localStorage.setItem('skit_user', JSON.stringify(u));
      })
      .catch((err) => {
        // Only a genuine auth failure (401 — no/invalid/expired token) means
        // actually logged out. Anything else (429 rate-limited, a transient
        // 5xx, a network blip) is NOT "logged out" — treating it as such was
        // wiping a valid session and forcing LoginRedirect's cross-origin
        // bounce (Portal → MaterialWatch → back to Portal) on every hiccup,
        // which looked exactly like "clicking the module card does nothing."
        // Leave the localStorage-cached user (from initial state) in place
        // and let the next request retry instead.
        if (err.response?.status === 401) {
          localStorage.removeItem('skit_token');
          localStorage.removeItem('skit_user');
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (credentials) => {
    const res = await authApi.login(credentials);
    const { token, user: u, devUserSwitchEnabled: switchEnabled } = res.data.data;
    localStorage.setItem('skit_token', token);
    localStorage.setItem('skit_user', JSON.stringify(u));
    setUser(u);
    setDevUserSwitchEnabled(!!switchEnabled);
    return u;
  };

  // Instantly switches to one of the four fixed dev-seed identities without a
  // password. Only ever reachable when devUserSwitchEnabled is true AND the
  // caller is already a super_admin — both enforced again on the backend.
  const devSwitchUser = async (target) => {
    const res = await authApi.devSwitch(target);
    const { token, user: u, devUserSwitchEnabled: switchEnabled } = res.data.data;
    localStorage.setItem('skit_token', token);
    localStorage.setItem('skit_user', JSON.stringify(u));
    setUser(u);
    setDevUserSwitchEnabled(!!switchEnabled);
    return u;
  };

  const logout = () => {
    authApi.logout().catch(() => {});
    localStorage.removeItem('skit_token');
    localStorage.removeItem('skit_user');
    setUser(null);
  };

  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem('skit_user', JSON.stringify(next));
      return next;
    });
  };

  // Only super_admin auto-bypasses. 'admin' is scoped to Portal user
  // management (see isAdmin below, still used for that) — its actual
  // MaterialWatch access depends on assigned permission groups/modules, same
  // as any other user.
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.permissions === 'ALL') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(permission);
  };

  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return Array.isArray(user.moduleAccess) && user.moduleAccess.includes(module);
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Driven by the acting Permission Group's Site Access level, NOT merely
  // having a siteId — a manager can be assigned a home site while still
  // holding "All Sites" access. super_admin is never restricted.
  const isSiteRestricted = user?.role !== 'super_admin' && user?.siteAccessLevel === 'ASSIGNED_ONLY';

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading, hasPermission, hasModuleAccess, isAdmin, isSiteRestricted, devUserSwitchEnabled, devSwitchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
